import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ForkKnife, Plus, Minus, Trash, PaperPlaneTilt, CheckCircle,
  Coffee, IceCream, BowlFood, Clock, CookingPot, Bell,
  CreditCard, Receipt, Star, ArrowLeft, Package, Truck, Storefront
} from "@phosphor-icons/react";
import { optimizeImageUrl } from "@/utils/imageOptimizer";
import ModifierModal from "@/components/ModifierModal";
import InstallAppButton from "@/components/InstallAppButton";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "All", icon: ForkKnife },
  { id: "appetizers", label: "Appetizers", icon: BowlFood },
  { id: "mains", label: "Mains", icon: ForkKnife },
  { id: "drinks", label: "Drinks", icon: Coffee },
  { id: "desserts", label: "Desserts", icon: IceCream },
];

const TIP_PRESETS = [
  { label: "15%", value: 0.15 },
  { label: "18%", value: 0.18 },
  { label: "20%", value: 0.20 },
  { label: "25%", value: 0.25 },
];

const SelfOrderPage = () => {
  const [sessionCode, setSessionCode] = useState("");
  const [session, setSession] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [guestName, setGuestName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  // Modifier picker for customer-facing menu. When set, the ModifierModal
  // opens for the tapped item and stashes the selection into the cart on
  // confirm. Items with no modifiers add directly (single-tap).
  const [modifierItem, setModifierItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTracker, setShowTracker] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [view, setView] = useState("menu"); // menu | bill | pay | paid
  const [billData, setBillData] = useState(null);
  const [tipPercent, setTipPercent] = useState(null);
  const [customTip, setCustomTip] = useState("");
  const [payForm, setPayForm] = useState({ card_number: "", expiration_date: "", cvv: "", first_name: "", last_name: "" });
  const [isPaying, setIsPaying] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [taxRate, setTaxRate] = useState(0.08);
  const wsRef = useRef(null);
  // Public online ordering (pickup / delivery, no table):
  // ``fulfillment === "dine_in"`` keeps the legacy table-code flow. On
  // ``pickup`` / ``delivery`` we load the public menu without a session,
  // collect customer info + address, and submit to /online-order/create.
  const [fulfillment, setFulfillment] = useState("dine_in");
  // Live delivery quote — populated by a debounced call to /delivery/quote
  // whenever the address changes on first-party delivery orders.
  const [deliveryQuote, setDeliveryQuote] = useState(null);   // { fee, eta_min, distance_mi, ok, reasons }
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [onlineConfig, setOnlineConfig] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({ name: "", phone: "", email: "", address: "" });
  const [publicOrderResult, setPublicOrderResult] = useState(null);

  useEffect(() => {
    // NOTE: previously this effect swapped the PWA manifest to
    // manifest-order.json so that installs from /order used a
    // customer-branded "Order Online" name.  Now that manifest.json
    // itself starts at /order (see /app/frontend/public/manifest.json)
    // and root / redirects to /order, the swap is unnecessary — and
    // actively harmful, because the RESTAURANT OWNER installing from
    // /order would end up with a "Order Online" home-screen icon
    // instead of their branded CrestPOS icon.  We keep manifest.json
    // (Crest POS) active on this page too so every install carries the
    // POS brand regardless of entry URL.
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      setSessionCode(code);
      loadMenu(code);
      return;
    }
    // Landing on /order with no code — fetch the public online-ordering
    // config so we can render the right set of Pickup / Delivery /
    // Dine-In buttons depending on the restaurant's toggles.
    axios.get(`${API}/online-order/config`)
      .then((r) => setOnlineConfig(r.data))
      .catch(() => setOnlineConfig({ pickup_enabled: false, delivery_enabled: false }));
  }, []);

  const loadPublicMenu = async (mode) => {
    setLoading(true);
    try {
      // Delivery uses the delivery-scoped menu (respects per-item toggles);
      // pickup uses the standard online menu.
      const menuUrl = mode === "delivery"
        ? `${API}/delivery/menu/public`
        : `${API}/online-order/menu`;
      const [menuRes, taxRes] = await Promise.all([
        axios.get(menuUrl),
        axios.get(`${API}/tax-rate`).catch(() => ({ data: { tax_rate: 8.0 } })),
      ]);
      // Fabricate a lightweight session object so the existing cart/menu
      // render code (which reads `session.menu_items`) works unchanged.
      setSession({
        table_name: mode === "pickup" ? "Pickup Order" : "Delivery Order",
        menu_items: menuRes.data,
      });
      setMenuItems(menuRes.data);
      setTaxRate((taxRes.data?.tax_rate || 8) / 100);
      setFulfillment(mode);
    } catch (e) {
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  };

  const submitPublicOrder = async () => {
    if (!customerInfo.name.trim() || !customerInfo.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    if (fulfillment === "delivery" && !customerInfo.address.trim()) {
      toast.error("Please enter a delivery address");
      return;
    }
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/online-order/create`, {
        fulfillment_type: fulfillment,
        items: cart.map((c) => ({
          menu_item_id: c.menu_item_id, quantity: c.quantity,
          modifiers: c.modifiers || [], notes: c.notes || "",
        })),
        customer_name: customerInfo.name.trim(),
        customer_phone: customerInfo.phone.trim(),
        customer_email: customerInfo.email.trim(),
        delivery_address: fulfillment === "delivery" ? customerInfo.address.trim() : undefined,
      });
      setPublicOrderResult(res.data);
      setOrderSuccess(true);
      toast.success(`Order placed! Ready in ${res.data.ready_estimate}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place order");
    } finally {
      setIsSubmitting(false);
    }
  };

  const connectTrackingWS = useCallback((code) => {
    const wsUrl = `${BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://")}/ws/track/${code}`;
    try {
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "status_update") {
            toast.info(`Order update: ${data.status_label}`);
            fetchTracking(code);
          }
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => { setTimeout(() => connectTrackingWS(code), 5000); };
      wsRef.current = ws;
    } catch (e) { /* ignore */ }
  }, []);

  const fetchTracking = async (code) => {
    try {
      const res = await axios.get(`${API}/self-order/track/${code || sessionCode}`);
      setTrackingData(res.data);
    } catch (e) { /* ignore */ }
  };

  const loadMenu = async (code) => {
    setLoading(true);
    try {
      const [menuRes, taxRes] = await Promise.all([
        axios.get(`${API}/self-order/menu/${code}`),
        axios.get(`${API}/tax-rate`).catch(() => ({ data: { tax_rate: 8.0 } }))
      ]);
      setSession(menuRes.data);
      setMenuItems(menuRes.data.menu_items);
      setTaxRate((taxRes.data.tax_rate || 8.0) / 100);
    } catch (error) {
      toast.error("Invalid or expired code. Please ask your server for a new one.");
    } finally { setLoading(false); }
  };

  const handleJoin = () => {
    if (sessionCode.length === 6) loadMenu(sessionCode);
    else toast.error("Please enter a valid 6-digit code");
  };

  const addToCart = (item, modifiers = [], notes = "") => {
    const modTotal = modifiers.reduce((s, m) => s + (m.price || 0), 0);
    const lineTotal = item.price + modTotal;
    // Items with modifiers or notes always create a new line so the customer
    // can order two "same" items differently (e.g. one salmon medium-rare,
    // one well-done). Plain items collapse into an existing line to keep
    // the cart short.
    if (modifiers.length === 0 && !notes) {
      const existing = cart.find(c => c.menu_item_id === item.id
        && (c.modifiers || []).length === 0 && !c.notes);
      if (existing) {
        setCart(cart.map(c => c === existing ? { ...c, quantity: c.quantity + 1 } : c));
        toast.success(`Added ${item.name}`);
        return;
      }
    }
    setCart([...cart, {
      id: `cart-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      menu_item_id: item.id,
      name: item.name,
      price: lineTotal,   // price stored is per-unit *including* modifiers
      base_price: item.price,
      quantity: 1,
      modifiers,
      notes,
      total: lineTotal,
    }]);
    toast.success(`Added ${item.name}`);
  };

  // Tap-handler for the Add button on the menu card. Opens the modifier
  // picker when the item has any modifiers (grouped OR item-specific);
  // otherwise falls through to the fast single-tap add.
  const handleTapAdd = (item) => {
    const hasGroups = (item.modifier_groups || []).length > 0;
    const hasFlat   = (item.modifiers || []).length > 0;
    if (hasGroups || hasFlat) {
      setModifierItem(item);
    } else {
      addToCart(item);
    }
  };

  const updateQty = (cartItemId, delta) => {
    setCart(cart.map(c => {
      if (c.id === cartItemId) {
        const newQty = c.quantity + delta;
        return newQty <= 0 ? null : { ...c, quantity: newQty };
      }
      return c;
    }).filter(Boolean));
  };

  const removeFromCart = (cartItemId) => setCart(cart.filter(c => c.id !== cartItemId));

  const cartTotal = cart.reduce((s, c) => s + c.total * c.quantity, 0);

  // Debounced first-party delivery quote — hits /delivery/quote whenever
  // the address or cart changes. Silent no-op when the restaurant isn't
  // running first-party delivery (falls through to legacy flat fee).
  useEffect(() => {
    if (fulfillment !== "delivery" || !onlineConfig?.first_party_delivery) {
      setDeliveryQuote(null);
      return;
    }
    const addr = (customerInfo.address || "").trim();
    if (addr.length < 6) {
      setDeliveryQuote(null);
      return;
    }
    setQuoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/delivery/quote`, {
          address: addr,
          subtotal: cartTotal,
        });
        setDeliveryQuote(res.data);
      } catch (e) {
        setDeliveryQuote({
          ok: false,
          reasons: [e.response?.data?.detail || "Couldn't quote this address."],
        });
      } finally {
        setQuoteLoading(false);
      }
    }, 700);
    return () => { clearTimeout(t); setQuoteLoading(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo.address, cartTotal, fulfillment, onlineConfig?.first_party_delivery]);
  const cartTax = cartTotal * taxRate;
  const cartGrandTotal = cartTotal + cartTax;

  const handleSubmitOrder = async () => {
    if (cart.length === 0) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/self-order/place`, {
        session_code: sessionCode,
        items: cart.map(c => ({ menu_item_id: c.menu_item_id, quantity: c.quantity, modifiers: c.modifiers, notes: c.notes })),
        guest_name: guestName || undefined
      });
      setOrderSuccess(true);
      setCart([]);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place order");
    } finally { setIsSubmitting(false); }
  };

  const fetchBill = async () => {
    try {
      const res = await axios.get(`${API}/self-order/bill/${sessionCode}`);
      setBillData(res.data);
      setView("bill");
    } catch (error) {
      toast.error("No active bill found");
    }
  };

  const tipAmount = tipPercent !== null
    ? Math.round((billData?.subtotal || 0) * tipPercent * 100) / 100
    : parseFloat(customTip) || 0;

  const handlePay = async () => {
    if (!payForm.card_number || !payForm.expiration_date || !payForm.cvv || !payForm.first_name || !payForm.last_name) {
      toast.error("Please fill in all card fields");
      return;
    }
    setIsPaying(true);
    try {
      const res = await axios.post(`${API}/self-order/pay`, {
        session_code: sessionCode,
        tip_amount: tipAmount,
        ...payForm
      });
      setPaymentResult(res.data);
      setView("paid");
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
    } finally { setIsPaying(false); }
  };

  const filteredItems = activeCategory === "all" ? menuItems : menuItems.filter(i => i.category === activeCategory);

  // ===== JOIN SCREEN =====
  if (!session) {
    const showPickup = !!onlineConfig?.pickup_enabled;
    const showDelivery = !!onlineConfig?.delivery_enabled;
    const anyPublic = showPickup || showDelivery;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative" data-testid="self-order-join">
        <InstallAppButton variant="floating" />
        {/* Escape hatch: staff who accidentally landed on the customer page can jump to /login */}
        <a
          href="/login"
          className="absolute top-4 left-4 text-xs text-muted-foreground/70 hover:text-foreground underline underline-offset-2"
          data-testid="employee-login-link"
        >
          Employee login
        </a>
        <Card className="w-full max-w-sm shadow-xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-3">
              <ForkKnife size={32} weight="fill" className="text-white" />
            </div>
            <CardTitle className="font-heading text-2xl">
              {onlineConfig?.company_name || "Order Online"}
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              {anyPublic ? "How would you like to order?" : "Enter the code from your table to start ordering"}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {anyPublic && (
              <>
                {showPickup && (
                  <Button
                    onClick={() => loadPublicMenu("pickup")}
                    className="w-full h-14 justify-start gap-3 text-base"
                    variant="outline"
                    disabled={loading}
                    data-testid="fulfillment-pickup-btn"
                  >
                    <Package size={22} weight="fill" className="text-primary" />
                    <div className="text-left">
                      <div className="font-medium">Pickup</div>
                      <div className="text-xs text-muted-foreground">
                        Ready in {onlineConfig?.pickup_ready_estimate || "20-30 min"}
                      </div>
                    </div>
                  </Button>
                )}
                {showDelivery && (
                  <Button
                    onClick={() => loadPublicMenu("delivery")}
                    className="w-full h-14 justify-start gap-3 text-base"
                    variant="outline"
                    disabled={loading}
                    data-testid="fulfillment-delivery-btn"
                  >
                    <Truck size={22} weight="fill" className="text-primary" />
                    <div className="text-left">
                      <div className="font-medium">Delivery</div>
                      <div className="text-xs text-muted-foreground">
                        {onlineConfig?.first_party_delivery
                          ? "We\u2019ll estimate the time once you enter your address"
                          : <>
                              {onlineConfig?.delivery_ready_estimate || "45-60 min"}
                              {onlineConfig?.delivery_fee > 0 && ` • $${onlineConfig.delivery_fee.toFixed(2)} fee`}
                            </>}
                      </div>
                    </div>
                  </Button>
                )}
                <div className="relative py-1">
                  <Separator />
                  <span className="absolute left-1/2 -translate-x-1/2 -top-2 bg-white px-2 text-[10px] uppercase tracking-wider text-muted-foreground">Dine-in</span>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Storefront size={14} /> Table Code
              </Label>
              <Input
                placeholder="000000"
                value={sessionCode}
                onChange={(e) => setSessionCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center font-mono text-2xl tracking-widest h-14"
                maxLength={6}
                data-testid="session-code-input"
              />
            </div>
            <Button onClick={handleJoin} className="w-full h-12" disabled={sessionCode.length !== 6 || loading} data-testid="join-session-btn">
              {loading ? "Loading..." : "Start Ordering"}
            </Button>
            {/* Add-to-Home-Screen — only renders when the browser fires
                beforeinstallprompt (Chrome/Edge/Android) or the guest is
                on iOS Safari. Hidden entirely on already-installed PWAs. */}
            <InstallAppButton className="pt-2" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== PAYMENT SUCCESS =====
  if (view === "paid" && paymentResult) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="payment-success">
        <Card className="w-full max-w-sm shadow-xl text-center">
          <CardContent className="pt-8 pb-6 px-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-green-600" weight="fill" />
            </div>
            <h2 className="font-heading text-2xl font-medium mb-1">Payment Successful</h2>
            <p className="text-muted-foreground text-sm mb-6">Thank you for dining with us!</p>
            <div className="bg-secondary/50 rounded-xl p-4 mb-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Amount Charged</span><span className="font-mono font-medium">${paymentResult.amount_charged.toFixed(2)}</span></div>
              {tipAmount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tip Included</span><span className="font-mono">${tipAmount.toFixed(2)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Transaction</span><span className="font-mono text-xs">{paymentResult.transaction_id}</span></div>
            </div>
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <Star size={12} weight="fill" className="text-yellow-500" />
              <span>We'd love your feedback!</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== PAY SCREEN =====
  if (view === "pay" && billData) {
    return (
      <div className="min-h-screen bg-background pb-6" data-testid="customer-pay-page">
        <div className="sticky top-0 z-10 bg-white border-b border-border p-4">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView("bill")} className="shrink-0" data-testid="back-to-bill">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-medium">Payment</h1>
              <p className="text-xs text-muted-foreground">{billData.table_name}</p>
            </div>
          </div>
        </div>
        <div className="max-w-lg mx-auto p-4 space-y-4">
          {/* Tip Selection */}
          <Card>
            <CardContent className="p-4">
              <p className="font-medium text-sm mb-3">Add a Tip</p>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {TIP_PRESETS.map(tp => (
                  <button
                    key={tp.label}
                    onClick={() => { setTipPercent(tp.value); setCustomTip(""); }}
                    className={`py-3 rounded-xl text-center text-sm font-medium transition-colors ${tipPercent === tp.value ? "bg-primary text-white" : "bg-secondary hover:bg-secondary/80"}`}
                    data-testid={`tip-${tp.label}`}
                  >
                    <div>{tp.label}</div>
                    <div className="text-xs font-mono opacity-80">${(billData.subtotal * tp.value).toFixed(2)}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setTipPercent(null); setCustomTip("0"); }} className={`px-3 py-2 rounded-lg text-xs ${tipPercent === null && customTip !== "" ? "bg-primary text-white" : "bg-secondary"}`}>Custom</button>
                {tipPercent === null && (
                  <div className="flex items-center gap-1 flex-1">
                    <span className="text-sm text-muted-foreground">$</span>
                    <Input type="number" step="0.01" min="0" value={customTip} onChange={(e) => { setCustomTip(e.target.value); setTipPercent(null); }} className="h-9" placeholder="0.00" data-testid="custom-tip" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Card Info */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="font-medium text-sm mb-1">Card Details</p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-2">
                <p className="text-xs text-yellow-800">Test card: <span className="font-mono">4111 1111 1111 1111</span></p>
              </div>
              <div className="space-y-2">
                <Input placeholder="Card Number" value={payForm.card_number} onChange={(e) => setPayForm({ ...payForm, card_number: e.target.value.replace(/\D/g, "").slice(0, 16) })} className="font-mono" maxLength={16} data-testid="pay-card-number" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="MM/YY" value={payForm.expiration_date} onChange={(e) => setPayForm({ ...payForm, expiration_date: e.target.value })} className="font-mono" data-testid="pay-expiry" />
                <Input placeholder="CVV" type="password" value={payForm.cvv} onChange={(e) => setPayForm({ ...payForm, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono" maxLength={4} data-testid="pay-cvv" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="First Name" value={payForm.first_name} onChange={(e) => setPayForm({ ...payForm, first_name: e.target.value })} data-testid="pay-first-name" />
                <Input placeholder="Last Name" value={payForm.last_name} onChange={(e) => setPayForm({ ...payForm, last_name: e.target.value })} data-testid="pay-last-name" />
              </div>
            </CardContent>
          </Card>

          {/* Summary & Pay */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${billData.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax</span><span className="font-mono">${billData.tax.toFixed(2)}</span></div>
              {tipAmount > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tip</span><span className="font-mono">${tipAmount.toFixed(2)}</span></div>}
              <Separator />
              <div className="flex justify-between font-medium text-lg"><span>Total</span><span className="font-mono">${(billData.total + tipAmount).toFixed(2)}</span></div>
            </CardContent>
          </Card>

          <Button onClick={handlePay} className="w-full h-14 text-base gap-2" disabled={isPaying} data-testid="confirm-pay-btn">
            {isPaying ? "Processing..." : <><CreditCard size={20} weight="fill" /> Pay ${(billData.total + tipAmount).toFixed(2)}</>}
          </Button>
        </div>
      </div>
    );
  }

  // ===== VIEW BILL =====
  if (view === "bill" && billData) {
    return (
      <div className="min-h-screen bg-background pb-6" data-testid="customer-bill-page">
        <div className="sticky top-0 z-10 bg-white border-b border-border p-4">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView("menu")} className="shrink-0" data-testid="back-to-menu-from-bill">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-medium">Your Bill</h1>
              <p className="text-xs text-muted-foreground">{billData.table_name}</p>
            </div>
          </div>
        </div>
        <div className="max-w-lg mx-auto p-4">
          {billData.items?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Receipt size={48} className="mx-auto mb-2 opacity-30" />
                <p>No active orders</p>
                <Button onClick={() => setView("menu")} className="mt-4">Back to Menu</Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="mb-4">
                <CardContent className="p-4">
                  <div className="space-y-2">
                    {billData.items?.map((item, idx) => (
                      <div key={idx} className="flex items-start justify-between py-2 border-b border-dashed border-border/50 last:border-0">
                        <div>
                          <p className="text-sm font-medium"><span className="font-mono text-muted-foreground mr-2">{item.quantity}x</span>{item.name}</p>
                          {item.modifiers?.length > 0 && <p className="text-xs text-muted-foreground ml-6">{item.modifiers.join(", ")}</p>}
                        </div>
                        <span className="font-mono text-sm shrink-0">${item.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <Separator className="my-3" />
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${billData.subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span><span className="font-mono">${billData.tax.toFixed(2)}</span></div>
                    <Separator />
                    <div className="flex justify-between font-medium text-lg"><span>Total</span><span className="font-mono">${billData.total.toFixed(2)}</span></div>
                  </div>
                </CardContent>
              </Card>

              <Button onClick={() => setView("pay")} className="w-full h-14 text-base gap-2" data-testid="pay-now-btn">
                <CreditCard size={20} weight="fill" /> Pay Now - ${billData.total.toFixed(2)}
              </Button>
              <Button variant="outline" onClick={() => setView("menu")} className="w-full mt-2 h-12" data-testid="continue-ordering-btn">
                Continue Ordering
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ===== PUBLIC ORDER SUCCESS (pickup / delivery) =====
  if (orderSuccess && publicOrderResult) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="public-order-success">
        <InstallAppButton variant="floating" />
        <Card className="w-full max-w-sm shadow-xl text-center">
          <CardContent className="pt-8 pb-6 px-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-green-600" weight="fill" />
            </div>
            <h2 className="font-heading text-2xl font-medium mb-1">Order Placed</h2>
            <p className="text-muted-foreground text-sm mb-6">
              {fulfillment === "delivery"
                ? "We're preparing your order and dispatching a driver."
                : "We're preparing your order. See you soon!"}
            </p>
            <div className="bg-secondary/50 rounded-xl p-4 mb-4 space-y-2 text-sm text-left">
              <div className="flex justify-between"><span className="text-muted-foreground">Order #</span><span className="font-mono text-xs">{publicOrderResult.order_id?.slice(-8).toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground capitalize">{publicOrderResult.fulfillment_type}</span><span className="font-medium">{publicOrderResult.ready_estimate}</span></div>
              <Separator />
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${publicOrderResult.subtotal?.toFixed(2)}</span></div>
              {publicOrderResult.delivery_fee > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Delivery Fee</span><span className="font-mono">${publicOrderResult.delivery_fee.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono">${publicOrderResult.tax?.toFixed(2)}</span></div>
              <Separator />
              <div className="flex justify-between font-medium"><span>Total</span><span className="font-mono">${publicOrderResult.total?.toFixed(2)}</span></div>
            </div>
            {onlineConfig?.company_phone && (
              <p className="text-xs text-muted-foreground">Questions? Call {onlineConfig.company_phone}</p>
            )}
            {fulfillment === "delivery" && onlineConfig?.first_party_delivery && publicOrderResult.order_id && (
              <Button
                variant="outline"
                className="w-full gap-2 mt-3"
                onClick={() => window.open(`/track/${publicOrderResult.order_id}`, "_blank")}
                data-testid="track-order-btn"
              >
                <Truck size={16} weight="bold" /> Track My Delivery
              </Button>
            )}
            <Button
              onClick={() => {
                setOrderSuccess(false);
                setPublicOrderResult(null);
                setSession(null);
                setCart([]);
                setCustomerInfo({ name: "", phone: "", email: "", address: "" });
                setFulfillment("dine_in");
                setView("menu");
              }}
              variant="outline"
              className="w-full mt-4"
              data-testid="new-order-btn"
            >
              Place Another Order
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== PUBLIC CHECKOUT (pickup / delivery contact + address) =====
  if (view === "public_checkout" && fulfillment !== "dine_in") {
    // Prefer the live first-party quote fee; fall back to legacy flat fee.
    const usingQuote = fulfillment === "delivery" && !!deliveryQuote?.ok;
    const feeToAdd = fulfillment === "delivery"
      ? (usingQuote ? deliveryQuote.fee : (onlineConfig?.delivery_fee || 0))
      : 0;
    const previewTax = (cartTotal + feeToAdd) * taxRate;
    const previewTotal = cartTotal + feeToAdd + previewTax;
    // Block Place-Order when first-party quote failed (out of range,
    // under minimum, etc.). Legacy delivery has no live guardrail.
    const firstPartyBlocked = fulfillment === "delivery"
      && onlineConfig?.first_party_delivery
      && deliveryQuote
      && deliveryQuote.ok === false;
    return (
      <div className="min-h-screen bg-background pb-6" data-testid="public-checkout-page">
        <InstallAppButton variant="floating" />
        <div className="sticky top-0 z-10 bg-white border-b border-border p-4">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView("menu")} className="shrink-0" data-testid="back-to-menu-from-checkout">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-medium capitalize">{fulfillment} Checkout</h1>
              <p className="text-xs text-muted-foreground">Enter your contact details</p>
            </div>
          </div>
        </div>
        <div className="max-w-lg mx-auto p-4 space-y-4">
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  placeholder="Jane Doe"
                  data-testid="checkout-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  data-testid="checkout-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  type="email"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  placeholder="you@example.com"
                  data-testid="checkout-email"
                />
              </div>
              {fulfillment === "delivery" && (
                <div className="space-y-2">
                  <Label>Delivery Address</Label>
                  <Input
                    value={customerInfo.address}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                    placeholder="123 Main St, Apt 4B, City, State ZIP"
                    data-testid="checkout-address"
                  />
                  {onlineConfig?.first_party_delivery && (
                    <div className="text-xs" data-testid="delivery-quote-block">
                      {quoteLoading && (
                        <span className="text-muted-foreground">Checking distance…</span>
                      )}
                      {!quoteLoading && deliveryQuote?.ok && (
                        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-800 dark:text-emerald-200 p-2 space-y-0.5">
                          <div><b>${deliveryQuote.fee.toFixed(2)}</b> delivery fee · {deliveryQuote.distance_mi.toFixed(1)} mi</div>
                          <div>Ready in ~<b>{deliveryQuote.eta_min}</b> min from order time</div>
                        </div>
                      )}
                      {!quoteLoading && deliveryQuote && deliveryQuote.ok === false && (
                        <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 p-2 space-y-0.5" data-testid="delivery-quote-error">
                          {(deliveryQuote.reasons || ["Delivery unavailable for that address."]).map((r, i) => (
                            <div key={i}>{r}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${cartTotal.toFixed(2)}</span></div>
              {feeToAdd > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Delivery Fee</span><span className="font-mono">${feeToAdd.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span className="font-mono">${previewTax.toFixed(2)}</span></div>
              <Separator />
              <div className="flex justify-between font-medium text-base"><span>Total</span><span className="font-mono">${previewTotal.toFixed(2)}</span></div>
              <p className="text-[11px] text-muted-foreground pt-1">
                {usingQuote && deliveryQuote?.eta_min
                  ? <>Estimated delivery in ~<b>{deliveryQuote.eta_min}</b> min. </>
                  : <>Ready in {(fulfillment === "delivery"
                      ? onlineConfig?.delivery_ready_estimate
                      : onlineConfig?.pickup_ready_estimate) || "shortly"}. </>}
                Pay at {fulfillment === "delivery" ? "delivery" : "pickup"}.
              </p>
            </CardContent>
          </Card>

          <Button
            onClick={submitPublicOrder}
            className="w-full h-14 text-base gap-2"
            disabled={isSubmitting || firstPartyBlocked}
            data-testid="submit-public-order-btn"
          >
            {isSubmitting
              ? "Placing Order..."
              : firstPartyBlocked
                ? "Fix address to continue"
                : <><PaperPlaneTilt size={20} weight="fill" /> Place Order</>}
          </Button>
        </div>
      </div>
    );
  }

  // ===== ORDER TRACKER =====
  if (orderSuccess || showTracker) {
    return <OrderTracker sessionCode={sessionCode} tableName={session?.table_name} onOrderMore={() => { setOrderSuccess(false); setShowTracker(false); setView("menu"); }} onViewBill={fetchBill} fetchTracking={fetchTracking} trackingData={trackingData} connectWS={connectTrackingWS} />;
  }

  // ===== MAIN MENU =====
  const isPublicMode = fulfillment !== "dine_in";
  return (
    <div className="min-h-screen bg-background pb-32" data-testid="self-order-page">
      <InstallAppButton variant="floating" />
      <div className="sticky top-0 z-10 bg-white border-b border-border p-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isPublicMode && (
              <Button variant="ghost" size="icon" onClick={() => { setSession(null); setCart([]); setFulfillment("dine_in"); }} className="shrink-0" data-testid="back-to-landing-btn">
                <ArrowLeft size={20} />
              </Button>
            )}
            <div>
              <h1 className="font-heading text-lg font-medium">{session.table_name}</h1>
              {isPublicMode ? (
                <p className="text-xs text-muted-foreground capitalize">{fulfillment} order</p>
              ) : (
                <p className="text-xs text-muted-foreground">Code: {sessionCode}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isPublicMode && (
              <>
                <Button size="sm" variant="outline" className="gap-1 h-8" onClick={fetchBill} data-testid="view-bill-btn">
                  <Receipt size={14} /> Bill
                </Button>
                <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => { fetchTracking(sessionCode); setShowTracker(true); }} data-testid="track-order-btn">
                  <Clock size={14} /> Track
                </Button>
                {guestName ? (
                  <Badge variant="outline">{guestName}</Badge>
                ) : (
                  <Input placeholder="Your name" value={guestName} onChange={(e) => setGuestName(e.target.value)} className="w-24 h-8 text-xs" data-testid="guest-name" />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4">
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 no-scrollbar">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${activeCategory === cat.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:bg-secondary/80"}`} data-testid={`self-cat-${cat.id}`}>
                <Icon size={16} /> {cat.label}
              </button>
            );
          })}
        </div>

        <div className="space-y-3">
          {filteredItems.map((item, idx) => (
            <motion.div key={item.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }} className="bg-white rounded-xl border border-border p-3 flex gap-3" data-testid={`self-menu-${item.id}`}>
              {item.image_url && <img src={optimizeImageUrl(item.image_url, 100)} alt={item.name} width={80} height={80} loading="lazy" decoding="async" className="w-20 h-20 object-cover rounded-lg flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm">{item.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-mono font-medium text-primary">${item.price.toFixed(2)}</span>
                  <Button size="sm" onClick={() => handleTapAdd(item)} className="h-8 gap-1" data-testid={`add-${item.id}`}>
                    <Plus size={14} /> Add
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Cart Footer */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border shadow-lg z-20" data-testid="self-order-cart">
          <div className="max-w-lg mx-auto p-4">
            <div className="max-h-40 overflow-y-auto space-y-2 mb-3">
              {cart.map(item => (
                <div key={item.id} className="text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 bg-secondary rounded-lg">
                        <button onClick={() => updateQty(item.id, -1)} className="p-1 hover:bg-secondary/80 rounded" data-testid={`dec-${item.id}`}><Minus size={12} /></button>
                        <span className="font-mono text-xs w-5 text-center">{item.quantity}</span>
                        <button onClick={() => updateQty(item.id, 1)} className="p-1 hover:bg-secondary/80 rounded" data-testid={`inc-${item.id}`}><Plus size={12} /></button>
                      </div>
                      <span className="truncate max-w-[150px]">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">${(item.total * item.quantity).toFixed(2)}</span>
                      <button onClick={() => removeFromCart(item.id)} className="text-destructive" data-testid={`remove-cart-${item.id}`}><Trash size={14} /></button>
                    </div>
                  </div>
                  {/* Show the customer's picked modifiers + note in a
                      dim indent line so they can double-check before
                      submitting. */}
                  {(item.modifiers?.length > 0 || item.notes) && (
                    <p className="text-[10px] text-muted-foreground ml-[76px] leading-tight">
                      {(item.modifiers || []).map(m => m.name).join(", ")}
                      {item.notes && (item.modifiers?.length > 0 ? " · " : "")}
                      {item.notes && <em>{item.notes}</em>}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <Separator className="mb-3" />
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-muted-foreground">Subtotal</span><span className="font-mono">${cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-muted-foreground">Tax ({(taxRate * 100).toFixed(1)}%)</span><span className="font-mono">${cartTax.toFixed(2)}</span>
            </div>
            <Button
              onClick={() => {
                if (isPublicMode) setView("public_checkout");
                else handleSubmitOrder();
              }}
              className="w-full h-12 gap-2 text-base"
              disabled={isSubmitting}
              data-testid="submit-order-btn"
            >
              {isSubmitting ? "Placing Order..." : (
                <>
                  <PaperPlaneTilt size={20} weight="fill" />
                  {isPublicMode ? `Continue to Checkout - $${cartGrandTotal.toFixed(2)}` : `Place Order - $${cartGrandTotal.toFixed(2)}`}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Customer-facing modifier picker. Shares the same component as the
          POS-side ModifierModal so required-modifier enforcement, group
          badges, and quantity math stay consistent across every ordering
          channel. */}
      <ModifierModal
        open={!!modifierItem}
        item={modifierItem}
        onClose={() => setModifierItem(null)}
        onConfirm={(modifiers, notes) => {
          if (modifierItem) addToCart(modifierItem, modifiers, notes);
          setModifierItem(null);
        }}
      />
    </div>
  );
};

// Order Tracker Component
const OrderTracker = ({ sessionCode, tableName, onOrderMore, onViewBill, fetchTracking, trackingData, connectWS }) => {
  const [data, setData] = useState(trackingData);

  useEffect(() => {
    connectWS(sessionCode);
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/self-order/track/${sessionCode}`);
        setData(res.data);
      } catch (e) { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [sessionCode, connectWS]);

  useEffect(() => { if (trackingData) setData(trackingData); }, [trackingData]);

  const statusIcons = {
    pending: <Bell size={24} weight="fill" className="text-blue-500" />,
    in_progress: <CookingPot size={24} weight="fill" className="text-orange-500" />,
    ready: <CheckCircle size={24} weight="fill" className="text-green-500" />,
    served: <CheckCircle size={24} weight="fill" className="text-gray-400" />,
  };
  const statusColors = {
    pending: "border-blue-300 bg-blue-50",
    in_progress: "border-orange-300 bg-orange-50",
    ready: "border-green-300 bg-green-50",
    served: "border-gray-300 bg-gray-50",
  };
  const statusLabels = data?.status_labels || { pending: "Order received", in_progress: "Being prepared", ready: "Ready to serve", served: "Served" };
  const steps = ["pending", "in_progress", "ready", "served"];

  return (
    <div className="min-h-screen bg-background p-4" data-testid="order-tracker">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6 pt-4">
          <h1 className="font-heading text-2xl font-medium mb-1">Order Tracker</h1>
          <p className="text-muted-foreground text-sm">{tableName} | Code: {sessionCode}</p>
        </div>

        {!data || data.orders?.length === 0 ? (
          <Card className="text-center">
            <CardContent className="py-12">
              <Clock size={48} className="mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">No active orders to track</p>
              <Button onClick={onOrderMore} className="mt-4" data-testid="back-to-menu-btn">Back to Menu</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {data.orders.map((order) => {
              const currentStepIdx = steps.indexOf(order.status);
              return (
                <Card key={order.order_id} className={`${statusColors[order.status] || ""} border-2`} data-testid={`tracked-order-${order.order_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {statusIcons[order.status] || statusIcons.pending}
                        <div>
                          <p className="font-heading font-medium text-lg">{statusLabels[order.status]}</p>
                          <p className="text-xs text-muted-foreground">#{order.order_id.slice(-6)}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="capitalize font-mono">{order.status.replace("_", " ")}</Badge>
                    </div>
                    <div className="flex items-center gap-1 mb-5">
                      {steps.map((step, idx) => (
                        <div key={step} className="flex-1"><div className={`h-2 rounded-full transition-colors duration-500 ${idx <= currentStepIdx ? "bg-primary" : "bg-border"}`}></div></div>
                      ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground mb-5 px-1">
                      <span className={currentStepIdx >= 0 ? "text-primary font-medium" : ""}>Received</span>
                      <span className={currentStepIdx >= 1 ? "text-primary font-medium" : ""}>Preparing</span>
                      <span className={currentStepIdx >= 2 ? "text-primary font-medium" : ""}>Ready</span>
                      <span className={currentStepIdx >= 3 ? "text-primary font-medium" : ""}>Served</span>
                    </div>
                    <Separator className="mb-3" />
                    <div className="space-y-1">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{item.quantity}x {item.name}</span>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-3" />
                    <div className="flex justify-between font-medium"><span>Total</span><span className="font-mono">${order.total.toFixed(2)}</span></div>
                  </CardContent>
                </Card>
              );
            })}

            <div className="space-y-2 mt-4">
              <Button onClick={onViewBill} className="w-full h-12 gap-2 text-base" data-testid="tracker-pay-btn">
                <CreditCard size={18} weight="fill" /> View Bill & Pay
              </Button>
              <Button onClick={onOrderMore} variant="outline" className="w-full" data-testid="order-more-btn">
                Order More Items
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfOrderPage;
